import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { mutate, Arguments } from "swr";
import { useTenants } from "../hooks/useTenants";
import Loading from "../components/LoadingPage";
import { useIsConnected } from "../hooks/useIsConnected";
import { reconnect as reconnectChasables } from "./ChasablesContext";
import { useLocalStorage } from "../utils/localStorage";

const TenantContext = createContext<{
  tenant: string | null;
  updateTenant(tenant: string): Promise<void>;
}>({
  tenant: null,
  updateTenant: () => Promise.resolve(),
});

function TenantContextProvider({ children }: { children?: React.ReactNode }) {
  const isConnected = useIsConnected();
  const { tenants, isLoading, refetchTenants, isError } = useTenants();
  const storage = useLocalStorage();
  const storedTenantId = storage?.getItem<string>(storage.keys.tenant);
  const defaultTenant =
    tenants.find(({ id }) => id === storedTenantId)?.id ??
    tenants.find(({ isDefault }) => isDefault)?.id ??
    tenants[0]?.id;
  const [tenant, setTenant] = useState<string | null>(defaultTenant);

  useEffect(() => {
    if (isConnected) {
      refetchTenants();
    }
  }, [isConnected]);

  const updateTenant = useCallback(
    async (newTenant: string) => {
      if (tenant === newTenant) return;
      setTenant(newTenant);
      try {
        await new Promise<void>((resolve, reject) => {
          // setTimeout is required here to let react update the state firstly
          // then we revalidate all the swr caches and websocket connection
          setTimeout(() => {
            if (!isError) {
              Promise.all([mutate(mutateKeys, undefined, { revalidate: true }), reconnectChasables()])
                .then(() => resolve())
                .catch(reject);
            }
          }, 0);
        });
      } catch (e) {
        console.error(e);
      }
    },
    [tenant, isError]
  );

  const contextValue = useMemo(
    () => ({
      tenant: tenant || defaultTenant,
      updateTenant,
    }),
    [tenant, defaultTenant, updateTenant]
  );

  if (isLoading) {
    return <Loading />;
  }

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>;
}

export { TenantContext, TenantContextProvider };

function mutateKeys(cacheKey: Arguments) {
  if (typeof cacheKey !== "string") return false;
  const excludeKeys = [
    "/api/organisation/business-tenants",
    "/api/integrations/dynamics/companies",
    "/api/integrations/dynamics/settings",
    "/api/users",
  ];
  return excludeKeys.every((key) => !cacheKey.includes(key));
}
